import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

const ROLE_BADGE: Record<string, { label: string; className: string }> = {
  technician:  { label: 'Technician', className: 'bg-blue-100 text-blue-700' },
  manager:     { label: 'Manager',    className: 'bg-purple-100 text-purple-700' },
  admin:       { label: 'Admin',      className: 'bg-orange-100 text-orange-700' },
  super_admin: { label: 'Super Admin',className: 'bg-red-100 text-red-700' },
  management:  { label: 'Management', className: 'bg-purple-100 text-purple-700' },
};

function fmtDateTime(iso: string) {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

interface AONotesProps {
  aoId: string;
  currentUserId: string; // technicians.id of the logged-in user
}

export function AONotes({ aoId, currentUserId }: AONotesProps) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');

  const { data: notes, isLoading } = useQuery({
    queryKey: ['ao-notes', aoId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('assembly_order_notes')
        .select(`
          id, note_text, created_at,
          author:technicians!created_by(name, alias, role)
        `)
        .eq('ao_id', aoId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!aoId,
  });

  const addNote = useMutation({
    mutationFn: async (text: string) => {
      const { error } = await supabase
        .from('assembly_order_notes')
        .insert({ ao_id: aoId, note_text: text.trim(), created_by: currentUserId });
      if (error) throw error;
    },
    onSuccess: () => {
      setDraft('');
      queryClient.invalidateQueries({ queryKey: ['ao-notes', aoId] });
    },
    onError: (err: any) => toast.error(`Failed to add note: ${err.message}`),
  });

  const handleSubmit = () => {
    if (draft.trim()) addNote.mutate(draft);
  };

  return (
    <div className="space-y-3">
      {/* Notes list */}
      <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
        {isLoading && (
          <p className="text-xs text-muted-foreground text-center py-4">Loading notes…</p>
        )}
        {!isLoading && notes?.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-4">No notes yet.</p>
        )}
        {notes?.map((note: any) => {
          const author = note.author;
          const displayName = author?.alias || author?.name || 'Unknown';
          const role = author?.role ?? 'technician';
          const badge = ROLE_BADGE[role] ?? ROLE_BADGE.technician;
          return (
            <div key={note.id} className="rounded-md border bg-muted/30 p-3 space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-semibold">{displayName}</span>
                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${badge.className}`}>
                  {badge.label}
                </span>
                <span className="text-[10px] text-muted-foreground ml-auto">
                  {fmtDateTime(note.created_at)}
                </span>
              </div>
              <p className="text-sm whitespace-pre-wrap">{note.note_text}</p>
            </div>
          );
        })}
      </div>

      {/* New note input */}
      <div className="flex gap-2">
        <textarea
          className="flex-1 p-2 text-sm border rounded-md resize-none bg-background focus:outline-none focus:ring-1 focus:ring-primary"
          rows={2}
          placeholder="Add a note…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSubmit();
          }}
          onClick={(e) => e.stopPropagation()}
        />
        <Button
          size="sm"
          className="self-end h-8 px-3"
          onClick={handleSubmit}
          disabled={!draft.trim() || addNote.isPending}
        >
          <Send className="h-3.5 w-3.5" />
        </Button>
      </div>
      <p className="text-[10px] text-muted-foreground">Ctrl+Enter to submit</p>
    </div>
  );
}
